import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, MEMORIZE_CATEGORIES } from "@/lib/constants";
import LibraryPicker from "@/components/LibraryPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Package, Library } from "lucide-react";

const empty = { name: "", category: "", description: "", items: [] };
const emptyItem = { designation: "", description: "", prestation_type: "matériel", quantity: 1, unit: "U", unit_price_ht: 0, tva_rate: 0, brand: "", model: "", library_id: null };

export default function PacksManager() {
  const [packs, setPacks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/packs");
    setPacks(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };

  const addItemFromLib = (it) => setForm((f) => ({
    ...f,
    items: [...f.items, {
      ...emptyItem, designation: it.designation, description: it.description || "",
      prestation_type: it.item_type || "matériel", unit: it.unit || "U",
      unit_price_ht: Number(it.sale_price || 0), tva_rate: Number(it.tva_rate || 0),
      brand: it.brand || "", model: it.model || "", library_id: it.id,
    }],
  }));
  const addBlankItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const updItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const rmItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom du pack est obligatoire"); return; }
    const payload = {
      name: form.name, category: form.category, description: form.description || "",
      items: form.items.map((it) => ({
        library_id: it.library_id || null, designation: it.designation, description: it.description || "",
        prestation_type: it.prestation_type || "matériel", quantity: Number(it.quantity || 1),
        unit: it.unit || "U", unit_price_ht: Number(it.unit_price_ht || 0), tva_rate: Number(it.tva_rate || 0),
        brand: it.brand || "", model: it.model || "",
      })),
    };
    try {
      if (editId) await api.put(`/packs/${editId}`, payload);
      else await api.post("/packs", payload);
      toast.success(editId ? "Pack mis à jour" : "Pack créé");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    try { await api.delete(`/packs/${deleteId}`); toast.success("Pack supprimé"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const packTotal = (p) => (p.items || []).reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price_ht || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="add-pack-button" onClick={openNew} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]"><Plus size={20} className="mr-1" />Nouveau pack</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {packs.length === 0 && <p className="text-slate-500">Aucun pack. Créez un pack pour regrouper plusieurs prestations.</p>}
        {packs.map((p) => (
          <div key={p.id} data-testid={`pack-card-${p.id}`} className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Package size={20} className="text-[#0284C7] shrink-0" />
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.category || "—"} · {(p.items || []).length} ligne(s)</div>
                </div>
              </div>
              <span className="font-bold text-[#16A34A] shrink-0">{eur(packTotal(p))}</span>
            </div>
            <div className="mt-3 space-y-1 text-sm text-slate-600 max-h-28 overflow-y-auto">
              {(p.items || []).map((it, i) => (
                <div key={i} className="flex justify-between gap-2"><span className="truncate">{it.quantity}× {it.designation}</span><span className="shrink-0">{eur(it.unit_price_ht)}</span></div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" data-testid={`edit-pack-${p.id}`} onClick={() => openEdit(p)} className="flex-1 border-[#0284C7] text-[#0284C7]"><Pencil size={15} className="mr-1" />Modifier</Button>
              <Button size="sm" variant="outline" data-testid={`delete-pack-${p.id}`} onClick={() => setDeleteId(p.id)} className="border-red-300 text-red-600"><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Modifier le pack" : "Nouveau pack"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nom du pack *</Label>
                <Input data-testid="pack-name-input" className="h-12" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex : Installation monosplit complète" />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category || undefined} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="pack-category-select" className="h-12"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{MEMORIZE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid="pack-description-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Lignes du pack ({form.items.length})</Label>
                <Button size="sm" variant="outline" data-testid="pack-add-from-lib" onClick={() => setPickerOpen(true)} className="border-[#0284C7] text-[#0284C7] font-bold"><Library size={16} className="mr-1" />Bibliothèque</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} data-testid={`pack-item-${i}`} className="grid grid-cols-12 gap-2 items-end border border-slate-100 rounded-md p-2">
                    <div className="col-span-6 space-y-1">
                      <Label className="text-[10px]">Désignation</Label>
                      <Input data-testid={`pack-item-desig-${i}`} className="h-10" value={it.designation} onChange={(e) => updItem(i, "designation", e.target.value)} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-[10px]">Qté</Label>
                      <Input type="number" step="0.5" data-testid={`pack-item-qty-${i}`} className="h-10" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} />
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-[10px]">P.U. HT</Label>
                      <Input type="number" step="0.01" data-testid={`pack-item-price-${i}`} className="h-10" value={it.unit_price_ht} onChange={(e) => updItem(i, "unit_price_ht", e.target.value)} />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="icon" variant="ghost" data-testid={`pack-item-remove-${i}`} onClick={() => rmItem(i)} className="text-red-600 h-10 w-10"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" data-testid="pack-add-blank" onClick={addBlankItem} className="h-10 w-full border-dashed border-slate-300 font-semibold"><Plus size={16} className="mr-1" />Ligne manuelle</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annuler</Button>
            <Button data-testid="save-pack-button" onClick={save} className="h-12 font-bold bg-[#16A34A] hover:bg-[#15803D]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LibraryPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={addItemFromLib} onCreateBlank={addBlankItem} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce pack ?</AlertDialogTitle>
            <AlertDialogDescription>Les devis existants ne seront pas affectés.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-pack" onClick={remove} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
